/**
 * CreditsScreen - In-game credits display for LPC asset attribution
 *
 * Loads credits.json produced by the LPC import tool and displays
 * all authors grouped by license type. Required for CC-BY, CC-BY-SA,
 * OGA-BY, and GPL license compliance.
 */
export default class CreditsScreen {
  constructor() {
    this.element = null;
    this._creditsData = null;
  }

  /**
   * Build the credits screen DOM content
   * @returns {HTMLElement}
   */
  async buildContent() {
    const el = document.createElement('div');
    el.className = 'credits-container';

    el.innerHTML = `
      <div class="credits-header">
        <div class="credits-title">Art Credits & Attribution</div>
        <div class="credits-subtitle">Character art from the Liberated Pixel Cup (LPC) community</div>
      </div>
      <div class="credits-body">
        <div class="credits-loading">Loading credits...</div>
      </div>
      <div class="credits-footer">
        All LPC assets used under their respective licenses.
        Share-alike (CC-BY-SA) applies to art derivatives only.
      </div>
    `;

    this.element = el;

    // Load and render credits asynchronously
    try {
      const resp = await fetch('/lpc/credits.json');
      this._creditsData = await resp.json();
      this._renderCredits(el.querySelector('.credits-body'));
    } catch (e) {
      el.querySelector('.credits-body').innerHTML =
        '<div class="credits-error">Could not load credits data.</div>';
    }

    return el;
  }

  _renderCredits(body) {
    const data = this._creditsData;
    if (!data || !data.authors) return;

    // Group authors by license
    const byLicense = new Map();
    for (const entry of data.authors) {
      for (const license of entry.licenses) {
        if (!byLicense.has(license)) byLicense.set(license, []);
        byLicense.get(license).push(entry);
      }
    }

    // License display order & labels
    const licenseOrder = [
      { key: 'CC0', label: 'CC0 (Public Domain)', desc: 'No restrictions' },
      { key: 'CC-BY 3.0', label: 'CC-BY 3.0', desc: 'Attribution required' },
      { key: 'CC-BY 4.0', label: 'CC-BY 4.0', desc: 'Attribution required' },
      { key: 'CC-BY-SA 3.0', label: 'CC-BY-SA 3.0', desc: 'Attribution + share-alike' },
      { key: 'CC-BY-SA 4.0', label: 'CC-BY-SA 4.0', desc: 'Attribution + share-alike' },
      { key: 'OGA-BY 3.0', label: 'OGA-BY 3.0', desc: 'OpenGameArt attribution' },
      { key: 'GPL 2.0', label: 'GPL 2.0', desc: 'Art assets only' },
      { key: 'GPL 3.0', label: 'GPL 3.0', desc: 'Art assets only' },
    ];

    let html = `<div class="credits-summary">${data.uniqueAuthors} contributors &middot; ${data.totalEntries} asset entries</div>`;

    for (const { key, label, desc } of licenseOrder) {
      const authors = byLicense.get(key);
      if (!authors || authors.length === 0) continue;

      // Deduplicate author names
      const uniqueAuthors = [...new Set(authors.map(a => a.author))].sort(
        (a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })
      );

      html += `
        <div class="credits-license-group">
          <div class="credits-license-header">
            <span class="credits-license-name">${label}</span>
            <span class="credits-license-desc">${desc}</span>
          </div>
          <div class="credits-author-list">
            ${uniqueAuthors.map(name => `<span class="credits-author">${this._escapeHtml(name)}</span>`).join('')}
          </div>
        </div>
      `;
    }

    body.innerHTML = html;
  }

  _escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  destroy() {
    this.element = null;
    this._creditsData = null;
  }
}
